"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useMemo } from "react";

/**
 * Program Cards 1070 — KUK "Explore other courses".
 *
 * Port of kuk-campus-portal `components/Degree/Explore.tsx`: the band that closes
 * every programme page — an eyebrow line over a heading, then the other programmes as
 * a four-up grid of image cards from 768px up and a horizontal rail below it, each
 * card carrying a View button.
 *
 * WHY IT IS NOT PROGRAM CARDS 1067. 1067 ports `OtherSpecialisations`, which shows the
 * SIBLINGS of the programme being viewed as text cards with a Brochure CTA and puts
 * its heading in a left column. This one shows OTHER PROGRAMMES as image cards with a
 * single View CTA over a full-bleed artwork band. The two appear on the same page —
 * a specialisation page renders both — so they cannot be one variant.
 *
 * THE ARTWORK IS A CSS BACKGROUND, not a `next/image`, because the reference stretches
 * it to `100% 100%` behind the whole band rather than fitting it; `background-size:
 * 100% 100%` has no `object-fit` equivalent that would survive `fill`.
 *
 * THE CARD TITLE PAIR is the same two lines at both widths, and which field leads
 * depends on the row: a specialisation leads with its parent programme and prints
 * "Parent(Specialisation)" underneath, a programme leads with its short name and
 * prints its full name. `meta_parent_title` is what decides, exactly as
 * `is_specialisation` does upstream.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:` is
 * 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see globals.css
 * `--breakpoint-*`), so every `md:` in the reference is ported as `tab:`.
 *
 * NOT CARRIED OVER — v1's `router.push` on a button, which rebuilds the href from
 * `is_specialisation_of` at click time and is invisible to a crawler. The card URL is
 * a stored field here and the CTA is a real link.
 */

interface CardRow {
  id?: string | number;
  /** Set on a specialisation: the parent programme, which then leads the card. */
  meta_parent_title?: string;
  meta_title?: string;
  /** The second line — the programme's full name. */
  meta_sub_title?: string;
  meta_image?: string;
  meta_alt_image?: string;
  meta_url?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ProgramCards1070Data {
  metadata?: CardRow[];
  /** Eyebrow line above the heading. */
  pre_title?: string;
  pre_title_color?: string;
  title?: string;
  title_text_color?: string;
  /** Full-bleed artwork behind the band. */
  background_image?: string;
  desktop_background_color?: string;
  /** Card surface / copy. */
  card_background_color?: string;
  salary_text_color?: string;
  /** View CTA. */
  cta_text?: string;
  cta_text_color?: string;
  cta_background_start_color?: string;
  /** Accent rule under the heading (v1's `.custom-underline`). */
  gradient_color_1?: string;
  status?: boolean | number;
}


/**
 * v1's `.custom-underline` — a short accent rule under the section heading, used on
 * 12 headings across the reference. Defined in ITS globals.css as a `::after` with
 * `bottom:-6px; left:0; width:33%; height:5px; background:#9f1c33`.
 *
 * Reproduced as a real element rather than a pseudo-element so the colour can come
 * from a CMS field (a pseudo-element cannot read an inline style). `w-fit` on the
 * heading is load-bearing: the rule is 33% of the HEADING, and a full-width h2 would
 * stretch it across a third of the page.
 */
const HEADING_UNDERLINE = "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

function normalizeHref(href: string): string {
  const h = (href || "").trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

const ProgramCards1070 = (props: { data?: ProgramCards1070Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as
    | ProgramCards1070Data
    | undefined;

  const {
    pre_title = "Didn't find what you need?",
    pre_title_color = "#101112",
    title = "Explore other courses",
    title_text_color = "#101112",
    background_image = "",
    desktop_background_color = "#FFFFFF",
    card_background_color = "#FFFFFF",
    salary_text_color = "#4A5568",
    cta_text = "View",
    cta_text_color = "#9F1C33",
    cta_background_start_color = "#F7F7F7",
    gradient_color_1 = "#9F1C33",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const cards = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [] as CardRow[];
    return rows
      .filter((r) => r?.meta_status !== false && (r?.meta_title || r?.meta_sub_title))
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  if (!status || cards.length === 0) return null;

  const onCtaClick = (card: CardRow) =>
    ctaClick({
      section: "program_cards_1070",
      component: "explore_courses_view",
      label: card.meta_title || cta_text || "view",
      step: "1",
      entity: "program_cards_1070",
      parentEntity: "program_cards_1070",
    });

  /** The two title lines, in the order the row's shape dictates. */
  const titleLines = (card: CardRow) =>
    card.meta_parent_title
      ? {
          lead: card.meta_parent_title,
          sub: `${card.meta_sub_title || card.meta_parent_title}(${card.meta_title || ""})`,
        }
      : { lead: card.meta_title || "", sub: card.meta_sub_title || "" };

  const cardCta = (card: CardRow) =>
    cta_text ? (
      <div className="flex w-full justify-end p-4">
        <Link
          href={normalizeHref(card.meta_url || "")}
          onClick={() => onCtaClick(card)}
          className="rounded-md border px-5 py-2 text-sm font-bold"
          style={{
            backgroundColor: cta_background_start_color,
            borderColor: cta_background_start_color,
            color: cta_text_color,
          }}
        >
          {cta_text}
        </Link>
      </div>
    ) : null;

  const cardImage = (card: CardRow) =>
    card.meta_image ? (
      <Image
        src={card.meta_image}
        alt={card.meta_alt_image || card.meta_title || ""}
        width={400}
        height={144}
        loading="lazy"
        className="h-36 w-full rounded-t-lg object-cover object-top"
      />
    ) : null;

  return (
    <section
      className="w-full overflow-hidden p-5 py-10 tab:p-20"
      style={{
        backgroundColor: desktop_background_color,
        ...(background_image
          ? {
              backgroundImage: `url('${background_image}')`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "100% 100%",
            }
          : {}),
      }}
    >
      <div className="relative mx-auto w-full max-w-[1120px]">
        {pre_title ? (
          <p className="text-sm" style={{ color: pre_title_color }}>
            {pre_title}
          </p>
        ) : null}
        {title ? (
          <h2
            className="relative mt-2 w-fit text-2xl font-bold tab:text-4xl"
            style={{ color: title_text_color }}
          >
            {title}
            <span
              aria-hidden
              className={HEADING_UNDERLINE}
              style={{ backgroundColor: gradient_color_1 }}
            />
          </h2>
        ) : null}

        {/* ── tab and up: four-up grid ──────────────────────────────────── */}
        <div className="hidden items-stretch justify-between gap-5 py-10 tab:grid tab:grid-cols-4">
          {cards.map((card, index) => {
            const lines = titleLines(card);
            return (
              <div
                key={card.id ?? index}
                className="flex h-[320px] max-h-[320px] w-full flex-col justify-between rounded-lg shadow"
                style={{ backgroundColor: card_background_color }}
              >
                <div>
                  {cardImage(card)}
                  <p
                    className="px-6 pt-5 text-lg font-bold"
                    style={{ color: title_text_color }}
                  >
                    {lines.lead}
                  </p>
                  {lines.sub ? (
                    <p
                      className="px-6 pt-2 text-xs font-bold"
                      style={{ color: salary_text_color }}
                    >
                      {lines.sub}
                    </p>
                  ) : null}
                </div>
                {cardCta(card)}
              </div>
            );
          })}
        </div>

        {/* ── below tab: horizontal rail ────────────────────────────────── */}
        <div className="relative tab:hidden">
          <div
            className="scrollbar-hide flex gap-4 overflow-x-auto py-10"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {cards.map((card, index) => {
              const lines = titleLines(card);
              return (
                <div
                  key={card.id ?? index}
                  className={cn(
                    "flex h-[330px] max-h-[330px] min-w-[260px] flex-col",
                    "justify-between rounded-lg shadow",
                  )}
                  style={{ backgroundColor: card_background_color }}
                >
                  <div>
                    {cardImage(card)}
                    <p
                      className="px-6 pb-2 pt-5 text-2xl"
                      style={{ color: title_text_color }}
                    >
                      {lines.lead}
                    </p>
                    {lines.sub ? (
                      <p
                        className="px-6 text-xs font-bold"
                        style={{ color: salary_text_color }}
                      >
                        {lines.sub}
                      </p>
                    ) : null}
                  </div>
                  {cardCta(card)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProgramCards1070;
